import {Routes} from '@angular/router';

/**
 * Which in-app URLs sit behind the auth guard (#34).
 *
 * Derived from the router configuration rather than a second hardcoded list, so adding or
 * removing a guarded route cannot leave this out of step. Callers pass the live
 * `Router.config` and the guard to look for.
 *
 * Used to decide what a 401 means: on a guarded URL the user must be sent to login, but on an
 * intentionally public one (`/graph`, `/spatial-data`) the stale session is cleared in place
 * and the visitor stays where they are with their selections intact.
 */
export function guardedRoutePatterns(routeConfig: Routes, guard: unknown): string[] {
  return (routeConfig || [])
    .filter(route => Array.isArray(route.canActivate) && route.canActivate.indexOf(guard as never) !== -1)
    .map(route => route.path || '')
    .filter(path => path !== '');
}

const segmentsOf = (value: string): string[] =>
  (value || '').split('?')[0].split('#')[0].split('/').filter(segment => segment !== '');

const matchesPattern = (urlSegments: string[], pattern: string): boolean => {
  const patternSegments = segmentsOf(pattern);

  if (patternSegments.length !== urlSegments.length) {
    return false;
  }

  return patternSegments.every(
    (segment, index) => segment.startsWith(':') || segment === urlSegments[index]
  );
};

export function isGuardedUrl(url: string, routeConfig: Routes, guard: unknown): boolean {
  const urlSegments = segmentsOf(url);

  if (urlSegments.length === 0) {
    return false;
  }

  return guardedRoutePatterns(routeConfig, guard).some(pattern => matchesPattern(urlSegments, pattern));
}
